import React, { useState, useEffect, useRef } from 'react';
import { Search, Phone, User, Mail, Building, X, RefreshCw } from 'lucide-react';
import contactSyncService from '../services/ContactSyncService';

const ContactSelector = ({ 
  onSelect, 
  onClose, 
  isOpen, 
  appId, 
  userId, 
  placeholder = "Search contacts...",
  showSyncButton = true 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [error, setError] = useState(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      loadContacts();
      getSyncStatus();
      // Focus search input
      setTimeout(() => {
        if (searchRef.current) {
          searchRef.current.focus();
        }
      }, 100);
    }
  }, [isOpen, appId, userId]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredContacts(contacts);
    } else {
      const filtered = contacts.filter(contact => {
        const searchLower = searchTerm.toLowerCase();
        const nameMatch = contact.name.toLowerCase().includes(searchLower);
        const phoneMatch = contact.phones?.some(phone => 
          phone.number.replace(/\D/g, '').includes(searchTerm.replace(/\D/g, ''))
        );
        const emailMatch = contact.emails?.some(email => 
          email.address.toLowerCase().includes(searchLower)
        );
        return nameMatch || phoneMatch || emailMatch;
      });
      setFilteredContacts(filtered);
    }
  }, [searchTerm, contacts]);

  const loadContacts = async () => {
    if (!appId || !userId) return;

    setLoading(true);
    setError(null);

    try {
      const { success, contacts: syncedContacts, message } = await contactSyncService.getSyncedContacts(appId, userId);
      
      if (success) {
        setContacts(syncedContacts);
        setFilteredContacts(syncedContacts);
      } else {
        setError(message);
        setContacts([]);
        setFilteredContacts([]);
      }
    } catch (error) {
      console.error('Error loading contacts:', error);
      setError('Error loading contacts');
      setContacts([]);
      setFilteredContacts([]);
    } finally {
      setLoading(false);
    }
  };

  const getSyncStatus = async () => {
    try {
      const status = await contactSyncService.getSyncStatus();
      setSyncStatus(status);
    } catch (error) {
      console.error('Error getting sync status:', error);
    }
  };

  const handleSyncContacts = async () => {
    if (!appId || !userId) return;

    setSyncing(true);
    setError(null);

    try {
      // Check permission first
      const permission = await contactSyncService.checkPermission();
      
      if (!permission.granted) {
        const requestResult = await contactSyncService.requestPermission();
        if (!requestResult.granted) {
          setError('Contacts permission is required to sync contacts');
          setSyncing(false);
          return;
        }
      }

      // Sync contacts
      const result = await contactSyncService.syncContactsToDatabase(userId, appId);
      
      if (result.success) {
        // Reload contacts after sync
        await loadContacts();
        await getSyncStatus();
      } else {
        setError(result.message);
      }
    } catch (error) {
      console.error('Error syncing contacts:', error);
      setError('Error syncing contacts');
    } finally {
      setSyncing(false);
    }
  };

  const handleSelectContact = (contact) => {
    onSelect(contact);
    onClose();
  };

  const formatPhoneNumber = (phone) => {
    // Simple phone number formatting
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `+91 ${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
    }
    return phone;
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[60] p-0 sm:p-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
      <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full max-w-2xl max-h-[92dvh] flex flex-col">
        {/* Header */}
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Select Contact</h2>
            <p className="text-sm text-gray-600">
              {contacts.length > 0 
                ? `${contacts.length} contacts available` 
                : 'No contacts synced yet'
              }
            </p>
          </div>
          <button
            onClick={onClose}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors touch-manipulation"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Search and Sync */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                ref={searchRef}
                type="text"
                placeholder={placeholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            {showSyncButton && (
              <button
                onClick={handleSyncContacts}
                disabled={syncing}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                <span>{syncing ? 'Syncing...' : 'Sync'}</span>
              </button>
            )}
          </div>

          {/* Sync Status */}
          {syncStatus && (
            <div className="mt-3 text-xs text-gray-500">
              {syncStatus.lastSyncDate ? (
                <span>Last synced: {syncStatus.lastSyncDate}</span>
              ) : (
                <span>Never synced</span>
              )}
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="px-6 py-3 bg-red-50 border-b border-red-200">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Contacts List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Loading contacts...</p>
              </div>
            </div>
          ) : filteredContacts.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-2">
                  {contacts.length === 0 
                    ? 'No contacts synced yet' 
                    : 'No contacts match your search'
                  }
                </p>
                {contacts.length === 0 && showSyncButton && (
                  <button
                    onClick={handleSyncContacts}
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Sync contacts now
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredContacts.map((contact) => (
                <div
                  key={contact.id}
                  onClick={() => handleSelectContact(contact)}
                  className="px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <div className="flex items-center space-x-4">
                    {/* Avatar */}
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-blue-600 font-medium text-sm">
                        {getInitials(contact.name)}
                      </span>
                    </div>

                    {/* Contact Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-medium text-gray-900 truncate">
                        {contact.name}
                      </h3>
                      
                      <div className="flex items-center space-x-4 mt-1">
                        {/* Phone Numbers */}
                        {contact.phones && contact.phones.length > 0 && (
                          <div className="flex items-center space-x-1">
                            <Phone className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-600">
                              {contact.phones[0].number}
                            </span>
                            {contact.phones.length > 1 && (
                              <span className="text-xs text-gray-500">
                                +{contact.phones.length - 1} more
                              </span>
                            )}
                          </div>
                        )}

                        {/* Email */}
                        {contact.emails && contact.emails.length > 0 && (
                          <div className="flex items-center space-x-1">
                            <Mail className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-600 truncate">
                              {contact.emails[0].address}
                            </span>
                          </div>
                        )}

                        {/* Organization */}
                        {contact.organization && (
                          <div className="flex items-center space-x-1">
                            <Building className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-600 truncate">
                              {contact.organization}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              {filteredContacts.length} of {contacts.length} contacts
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactSelector;
